import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { PersonaMarkdown } from '../personas/PersonaMarkdown';
import { gymnasiumService } from '../../services/gymnasiumService';
import { Gauntlet } from '../../types/gymnasium';
import { FiPlus, FiSearch, FiTrash2, FiSend, FiCheck, FiX, FiEdit, FiChevronDown, FiChevronRight, FiCpu, FiPlay, FiTarget } from 'react-icons/fi';
import GymSubHeader from './GymSubHeader';
import UserBadge from '../common/UserBadge';

const GauntletLibrary: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [gauntlets, setGauntlets] = useState<Gauntlet[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // AI generation state
  const [showGenerator, setShowGenerator] = useState(false);
  const [intent, setIntent] = useState('');
  const [scenarioCount, setScenarioCount] = useState(6);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await gymnasiumService.listGauntlets(undefined, search || undefined);
      setGauntlets(data);
    } catch (err) {
      console.error('Failed to load gauntlets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search]);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete gauntlet "${name}"?`)) return;
    try {
      await gymnasiumService.deleteGauntlet(id);
      setGauntlets(gs => gs.filter(g => g.id !== id));
    } catch (err) {
      console.error('Failed to delete gauntlet:', err);
    }
  };

  const handleGenerate = async () => {
    if (!intent.trim()) return;
    setGenerating(true);
    setError('');
    setGenerated(null);
    try {
      const result = await gymnasiumService.generateGauntlet(intent, scenarioCount);
      setGenerated(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveGenerated = async () => {
    if (!generated) return;
    setSaving(true);
    setError('');
    try {
      await gymnasiumService.createGauntlet(generated);
      setShowGenerator(false);
      setGenerated(null);
      setIntent('');
      load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
  } border focus:outline-none focus:ring-2 focus:ring-amber-500/30`;

  return (
    <div className="space-y-4">
      <GymSubHeader
        icon={<FiTarget className="w-4 h-4" />}
        title="Gauntlet Library"
        subtitle={`${gauntlets.length} scenario packs`}
        accentFrom="from-violet-400"
        accentTo="to-purple-400"
        actions={
          <button
            onClick={() => { setShowGenerator(true); setGenerated(null); setIntent(''); setError(''); }}
            className="px-3 py-1.5 text-xs rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all flex items-center gap-1.5"
          >
            <FiPlus className="w-3.5 h-3.5" /> Create
          </button>
        }
      />

      {/* Search */}
      <div className="relative">
        <FiSearch className={`absolute left-3 top-3 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        <input
          type="text"
          placeholder="Search gauntlets..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`${inputClass} pl-10`}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className={`h-32 rounded-xl animate-pulse ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} />
          ))}
        </div>
      ) : gauntlets.length === 0 ? (
        <div className={`${sectionClass} text-center py-12`}>
          <p className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>No gauntlets found</p>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Create your first scenario test pack.</p>
          <button
            onClick={() => setShowGenerator(true)}
            className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white"
          >
            Create Gauntlet
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gauntlets.map(g => (
            <div
              key={g.id}
              className={`${sectionClass} hover:scale-[1.01] transition-transform cursor-pointer`}
              onClick={() => navigate(`/gymnasium/gauntlets/${g.id}`)}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className={`text-sm font-semibold truncate flex-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {g.name.replace(/_/g, ' ')}
                </h3>
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(g.id, g.name); }}
                  className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}
                >
                  <FiTrash2 className="w-3 h-3" />
                </button>
              </div>
              <p className={`text-xs line-clamp-2 mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {g.description || 'No description'}
              </p>
              {(g as any).owner !== undefined && (
                <div className="mb-2">
                  <UserBadge owner={(g as any).owner} size="sm" />
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex gap-1 flex-wrap">
                  {g.tags.slice(0, 3).map(t => (
                    <span key={t} className="px-1.5 py-0.5 text-[9px] rounded bg-amber-600/20 text-amber-300">{t}</span>
                  ))}
                </div>
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{g.scenario_count} scenarios</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Gauntlet Generator Modal */}
      {showGenerator && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => !generating && setShowGenerator(false)}>
          <div
            className={`w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-6 py-4 border-b flex items-center gap-3 shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <FiCpu className="w-5 h-5 text-amber-400" />
              <div className="flex-1">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Create Gauntlet</h2>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Describe what you want to test — AI generates the scenarios</p>
              </div>
              <button onClick={() => !generating && setShowGenerator(false)} className={`p-1.5 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {error && (
                <div className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 text-sm">{error}</div>
              )}

              {/* Intent input */}
              {!generated && (
                <>
                  <div>
                    <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      What should this gauntlet test?
                    </label>
                    <textarea
                      className={`${inputClass} h-24`}
                      value={intent}
                      onChange={e => setIntent(e.target.value)}
                      placeholder="Describe what you want to test — or pick a template below..."
                      autoFocus
                    />
                  </div>

                  {/* Quick-pick template chips — 24 templates based on live persona roster */}
                  <div>
                    <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Quick Templates
                    </p>
                    <div className="flex flex-wrap gap-1.5 max-h-[180px] overflow-y-auto">
                      {[
                        // Architecture & Systems
                        { label: 'System Design Trade-offs', prompt: 'Test system architecture personas on distributed systems design: CAP theorem trade-offs, consistency models, database sharding strategies, event-driven vs request-driven architecture, and microservices boundary decisions. Include scenarios requiring practical cost-performance analysis.' },
                        { label: 'API Design Mastery', prompt: 'Evaluate API design expertise: RESTful resource modeling, GraphQL schema design, versioning strategies, rate limiting approaches, authentication patterns, pagination design, and error handling conventions. Test both greenfield design and legacy API evolution scenarios.' },
                        { label: 'Scalability Stress Test', prompt: 'Test personas on scaling challenges: handling 10x traffic spikes, database read replicas vs caching layers, CDN strategies, async processing with message queues, connection pooling, and graceful degradation under load. Include real-world failure scenarios.' },

                        // Security & Compliance
                        { label: 'Security Architecture', prompt: 'Evaluate security engineering: threat modeling a multi-tenant SaaS platform, zero-trust network design, secrets management, mTLS implementation, RBAC vs ABAC authorization models, supply chain security, and incident response planning. Include scenarios with competing security vs usability requirements.' },
                        { label: 'Compliance Gauntlet', prompt: 'Test compliance and governance knowledge: HIPAA technical safeguards, GDPR data processing impact assessments, SOC2 control implementation, PCI-DSS cardholder data protection, audit trail design, and cross-border data transfer mechanisms. Focus on practical implementation, not just theory.' },
                        { label: 'Cryptography Challenge', prompt: 'Evaluate cryptographic engineering: choosing encryption algorithms, key management lifecycle, secure random generation, certificate pinning strategies, TLS configuration hardening, and post-quantum readiness assessment. Include scenarios with performance constraints.' },

                        // Data & Engineering
                        { label: 'Data Pipeline Design', prompt: 'Test data engineering expertise: designing real-time streaming pipelines, batch vs micro-batch processing trade-offs, data lake organization, schema evolution strategies, data quality monitoring, and lineage tracking. Include scenarios with messy real-world data sources.' },
                        { label: 'Database Performance', prompt: 'Evaluate database expertise: query optimization for complex analytical queries, index design strategies, partition pruning, connection pool tuning, vacuum strategies for PostgreSQL, read replica lag handling, and migration planning for schema changes on large tables.' },
                        { label: 'ML/AI Integration', prompt: 'Test AI/ML integration knowledge: LLM prompt engineering best practices, RAG architecture design, embedding model selection, vector database indexing, model evaluation metrics, fine-tuning vs few-shot vs retrieval approaches, and handling hallucination mitigation in production.' },

                        // Frontend & UX
                        { label: 'Frontend Architecture', prompt: 'Evaluate frontend expertise: component architecture patterns, state management strategies (local vs global), performance optimization (bundle splitting, lazy loading, memoization), accessibility compliance, design system implementation, and responsive layout strategies for complex data-heavy UIs.' },
                        { label: 'Design Systems Audit', prompt: 'Test design systems knowledge: token architecture, component API design, theming strategies, cross-platform consistency, documentation standards, versioning and breaking changes, adoption metrics, and accessibility integration at the system level.' },
                        { label: 'UX Research Methods', prompt: 'Evaluate UX research capability: when to use qualitative vs quantitative methods, A/B test design and statistical significance, user interview techniques, usability heuristic evaluation, journey mapping methodology, and translating research findings into actionable product decisions.' },

                        // DevOps & Infrastructure
                        { label: 'Container Orchestration', prompt: 'Test DevOps expertise: Docker multi-stage build optimization, docker-compose service orchestration, health check design, volume management strategies, container security scanning, log aggregation patterns, and blue-green deployment with zero downtime. Include edge computing constraints.' },
                        { label: 'Observability Deep Dive', prompt: 'Evaluate observability knowledge: distributed tracing implementation, custom metric design, SLI/SLO definition, alert fatigue reduction strategies, log correlation across services, anomaly detection approaches, and debugging production issues with incomplete telemetry.' },
                        { label: 'Infrastructure as Code', prompt: 'Test IaC expertise: Terraform module design, state management strategies, drift detection, secrets handling in IaC, multi-environment promotion, cost estimation, and disaster recovery planning with infrastructure reproducibility.' },

                        // Product & Growth
                        { label: 'Product Strategy', prompt: 'Evaluate product thinking: prioritization frameworks (RICE, ICE, opportunity scoring), north star metric selection, feature discovery techniques, build-vs-buy analysis, product-market fit assessment, competitive moat evaluation, and roadmap communication strategies.' },
                        { label: 'Growth Engineering', prompt: 'Test growth expertise: activation funnel optimization, retention cohort analysis, viral coefficient calculation, A/B test architecture, feature flag strategies for experimentation, onboarding flow optimization, and ethical growth vs dark pattern identification.' },
                        { label: 'Go-to-Market Strategy', prompt: 'Evaluate GTM knowledge: market segmentation and ICP definition, pricing model design (usage-based, seat-based, hybrid), channel strategy selection, competitive positioning frameworks, launch sequencing, partner ecosystem development, and measuring GTM effectiveness.' },

                        // Cross-Disciplinary
                        { label: 'Technical Interview Panel', prompt: 'Simulate a senior engineering interview: system design (design a real-time collaboration platform), coding trade-offs (when to optimize vs ship), architecture review (critique a provided design), debugging methodology, and communication of technical decisions to non-technical stakeholders.' },
                        { label: 'Incident Response Drill', prompt: 'Test incident response capability: triage a cascading production failure (database connection exhaustion causing API timeouts causing frontend errors), communication during outage, root cause analysis methodology, post-mortem writing, and remediation action planning with prevention strategies.' },
                        { label: 'Ethics in Technology', prompt: 'Evaluate ethical reasoning: AI bias detection and mitigation, privacy-preserving data collection, algorithmic transparency requirements, responsible disclosure practices, accessibility as a human right, environmental impact of technology choices, and balancing innovation speed with societal impact.' },
                        { label: 'Cross-Team Communication', prompt: 'Test communication and leadership: explaining a complex technical decision to executives, writing an RFC for a controversial architecture change, facilitating a design review with opposing viewpoints, mentoring a junior engineer through a difficult debugging session, and writing a post-mortem that drives organizational learning.' },

                        // Specialized Domains
                        { label: 'Healthcare Tech', prompt: 'Evaluate healthcare technology expertise: FHIR resource modeling, clinical workflow integration, offline-first architecture for rural deployments, patient data de-identification techniques, clinical decision support design, and regulatory compliance for medical device software (SaMD).' },
                        { label: 'Edge Computing Resilience', prompt: 'Test edge computing knowledge: designing for intermittent connectivity, conflict resolution in distributed state, secure OTA update mechanisms, resource-constrained deployment optimization, local-first data processing, and failover strategies when cloud connectivity is lost.' },
                      ].map((t, i) => (
                        <button
                          key={i}
                          onClick={() => setIntent(t.prompt)}
                          className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                            intent === t.prompt
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                              : darkMode
                                ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-700/30'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div>
                      <label className={`text-xs font-medium mb-1 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Scenarios
                      </label>
                      <select
                        className={inputClass}
                        value={scenarioCount}
                        onChange={e => setScenarioCount(Number(e.target.value))}
                        style={{ width: 80 }}
                      >
                        {[3, 4, 5, 6, 8, 10, 12, 15].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1" />
                    <button
                      onClick={handleGenerate}
                      disabled={!intent.trim() || generating}
                      className="px-6 py-2.5 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 transition-all flex items-center gap-2"
                    >
                      {generating ? (
                        <><span className="animate-spin">&#9881;</span> Generating...</>
                      ) : (
                        <><FiSend className="w-4 h-4" /> Generate</>
                      )}
                    </button>
                  </div>
                </>
              )}

              {/* Generated preview */}
              {generated && (
                <>
                  {/* At-a-glance summary card */}
                  <div className={`rounded-lg border-l-4 p-3 ${darkMode ? 'bg-gray-800/50 border-green-600' : 'bg-green-50 border-green-500'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <FiCheck className="w-4 h-4 text-green-400" />
                        <span className={`text-sm font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          {(generated.name || '').replace(/_/g, ' ')}
                        </span>
                      </div>
                      <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {(generated.scenarios || []).length} scenarios
                      </span>
                    </div>
                    <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{generated.description}</p>
                    <div className="flex gap-1 flex-wrap">
                      {(generated.tags || []).map((t: string) => (
                        <span key={t} className="px-1.5 py-0.5 text-[9px] rounded bg-amber-600/20 text-amber-300">{t}</span>
                      ))}
                    </div>
                  </div>

                  {/* Scenario grid — at-a-glance, click to focus */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className={`text-[10px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Scenarios — click to inspect
                      </p>
                      {expandedScenario && (
                        <button onClick={() => setExpandedScenario(null)} className={`text-[10px] ${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}>
                          collapse
                        </button>
                      )}
                    </div>

                    {/* Compact chip row for at-a-glance view */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(generated.scenarios || []).map((s: any, i: number) => {
                        const isExpanded = expandedScenario === (s.label || i);
                        const diffColor = s.difficulty === 'hard' ? 'text-red-400' : s.difficulty === 'medium' ? 'text-amber-400' : 'text-green-400';
                        return (
                          <button
                            key={s.label || i}
                            onClick={() => setExpandedScenario(isExpanded ? null : (s.label || i))}
                            className={`px-2 py-1 rounded-lg text-xs transition-all ${
                              isExpanded
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 scale-[1.02]'
                                : darkMode ? 'bg-gray-700/40 text-gray-300 hover:bg-gray-700 border border-gray-700/30' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                            }`}
                          >
                            <span className="font-medium">{i + 1}.</span>{' '}
                            {(s.label || '').replace(/_/g, ' ')}
                            <span className={`ml-1.5 ${diffColor}`}>{'\u00b7'} {s.difficulty}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Expanded detail panel — only shows for focused scenario */}
                    {expandedScenario && (() => {
                      const s = (generated.scenarios || []).find((sc: any, i: number) => (sc.label || i) === expandedScenario);
                      if (!s) return null;
                      return (
                        <div className={`rounded-lg border p-3 ${darkMode ? 'border-amber-500/20 bg-gray-800/30' : 'border-amber-200 bg-amber-50/50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-bold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                              {(s.label || '').replace(/_/g, ' ')}
                            </span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                              {s.category} · {s.difficulty} · {s.max_score}pts
                            </span>
                          </div>
                          <div className="mb-2"><PersonaMarkdown content={s.prompt} size="small" /></div>
                          <p className={`text-[10px] font-semibold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>CRITERIA:</p>
                          <ul className="space-y-0.5">
                            {(s.criteria || []).map((c: string, ci: number) => (
                              <li key={ci} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{'\u2022'} {c}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {generated && (
              <div className={`px-6 py-4 border-t flex justify-between shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => { setGenerated(null); setError(''); }}
                  className={`px-4 py-2 text-sm rounded-lg flex items-center gap-2 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  <FiEdit className="w-3.5 h-3.5" /> Regenerate
                </button>
                <button
                  onClick={handleSaveGenerated}
                  disabled={saving}
                  className="px-6 py-2 text-sm rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 transition-all flex items-center gap-2"
                >
                  {saving ? 'Saving...' : <><FiCheck className="w-4 h-4" /> Save Gauntlet</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GauntletLibrary;
